/**
 * Regroupement des exercices d'un thème par mise en situation commune.
 *
 * Module pur (sans dépendance Supabase ni React) → testé en unitaire
 * (`theme-exercise-groups.test.ts`).
 */

import type { ThemeExerciseListItem } from './themes-api';

/**
 * Un groupe d'exercices affiché sous un même bandeau.
 * - `context` : exercices partageant la même mise en situation (`context_md`).
 * - `standalone` : exercice isolé, sans contexte partagé.
 */
export interface ThemeExerciseGroup {
  key: string;
  kind: 'context' | 'standalone';
  /** Markdown brut du contexte commun ; chaîne vide pour un groupe isolé. */
  heading: string;
  exercises: ThemeExerciseListItem[];
}

/**
 * Regroupe les exercices par `context_md` commun (comparé après `trim`).
 *
 * Deux exercices au contexte identique tombent dans le même groupe `context`.
 * Un exercice sans contexte forme un groupe `standalone` à lui seul.
 *
 * L'ordre d'entrée est préservé : un groupe se positionne là où apparaît son
 * premier exercice (le tri amont met déjà l'exercice primaire en tête).
 */
export function groupThemeExercises(exercises: ThemeExerciseListItem[]): ThemeExerciseGroup[] {
  const groups: ThemeExerciseGroup[] = [];
  const byContext = new Map<string, ThemeExerciseGroup>();

  for (const exercise of exercises) {
    const context = exercise.context_md?.trim();
    if (context) {
      const existing = byContext.get(context);
      if (existing) {
        existing.exercises.push(exercise);
        continue;
      }
      const group: ThemeExerciseGroup = {
        key: `context:${context}`,
        kind: 'context',
        heading: context,
        exercises: [exercise],
      };
      byContext.set(context, group);
      groups.push(group);
    } else {
      groups.push({
        key: `standalone:${exercise.id}`,
        kind: 'standalone',
        heading: '',
        exercises: [exercise],
      });
    }
  }

  return groups;
}
